import { useState } from 'react'
import type { TFunction } from 'i18next'
import type { SaleResource } from './types'

interface Objection {
  q: string
  a: string[]
}

export interface BriefEntry {
  term: string
  value: string
}

type PanelKey = 'objections' | 'resources' | 'product_brief'

export interface SalePanelState {
  id: string
  label: string
  open: boolean
  last: boolean
  onToggle: () => void
}

export interface ObjectionItem {
  id: string
  question: string
  title: string
  answers: string[]
  open: boolean
  last: boolean
  onToggle: () => void
}

export interface ResourceItem {
  type: string
  title: string
  onOpen: () => void
}

interface UseSalePanelsParams {
  t: TFunction
  saleType: string
  resources: SaleResource[]
  values: Record<string, string>
  highlightedValues: Record<string, string>
}

const RESOURCES: Array<{ type: string; title: string; opens: 'link' | 'modal' }> = [
  { type: 'learning_link', title: 'training_resources', opens: 'link' },
  { type: 'sale_script', title: 'sale_script_resources', opens: 'modal' },
  { type: 'email_template_link', title: 'email_template_resources', opens: 'link' },
  { type: 'objections', title: 'objections_resources', opens: 'modal' },
]

const listOf = <T>(t: TFunction, key: string, values: Record<string, string>): T[] => {
  const list = t(key, { returnObjects: true, defaultValue: [], fallbackLng: false, ...values }) as unknown

  return Array.isArray(list) ? (list as T[]) : []
}

const useSalePanels = ({ t, saleType, resources, values, highlightedValues }: UseSalePanelsParams) => {
  const [openPanel, setOpenPanel] = useState<string | null>(null)
  const [openObjection, setOpenObjection] = useState<number | null>(null)
  const [shownResource, setShownResource] = useState<{ title: string; html: string } | null>(null)
  const base = `additionalSales:${saleType}.panels`
  const objectionList = listOf<Objection>(t, `${base}.objections`, highlightedValues)
  const brief = listOf<BriefEntry>(t, `${base}.brief`, values)
  const rules = listOf<string>(t, `${base}.rules`, values)
  const note = t(`${base}.note`, { defaultValue: '', fallbackLng: false })

  const objections: ObjectionItem[] = objectionList.map((objection, index) => ({
    id: `objection-${index}`,
    question: objection.q,
    title: `« ${objection.q} »`,
    answers: objection.a,
    open: openObjection === index,
    last: index === objectionList.length - 1,
    onToggle: () => setOpenObjection(current => (current === index ? null : index)),
  }))

  const resourceItems: ResourceItem[] = RESOURCES.map(row => ({
    ...row,
    resource: resources.find(item => item.resource_type === row.type),
  }))
    .filter(row => row.resource?.resource_value)
    .map(row => {
      const title = t(`additionalSales:common.${row.title}`)
      const value = row.resource?.resource_value ?? ''

      return {
        type: row.type,
        title,
        onOpen: () => {
          if (row.opens === 'link') {
            window.open(value, '_blank', 'noopener')
            return
          }
          setShownResource({ title, html: value })
        },
      }
    })

  const presence: Array<[PanelKey, boolean]> = [
    ['objections', objections.length > 0],
    ['resources', resourceItems.length > 0],
    ['product_brief', brief.length > 0],
  ]
  const visibleKeys = presence.filter(([, shown]) => shown).map(([key]) => key)

  const panelOf = (key: PanelKey): SalePanelState | null => {
    const index = visibleKeys.indexOf(key)
    if (index === -1) return null

    return {
      id: `sale-panel-${key}`,
      label: t(`additionalSales:pulse.panels.${key}`),
      open: openPanel === key,
      last: index === visibleKeys.length - 1,
      onToggle: () => setOpenPanel(current => (current === key ? null : key)),
    }
  }

  const closeResource = () => setShownResource(null)

  return {
    panels: {
      objections: panelOf('objections'),
      resources: panelOf('resources'),
      productBrief: panelOf('product_brief'),
    },
    objections,
    resourceItems,
    brief,
    rules,
    hasRules: rules.length > 0,
    note,
    resourceModal: {
      isOpen: shownResource !== null,
      title: shownResource?.title ?? '',
      html: shownResource?.html ?? '',
    },
    closeResource,
  }
}

export default useSalePanels
